import simulationConfig from '../config/comfort-simulation.json'

export type HvacMode = 'idle' | 'cooling' | 'heating'
export type WaterSourceMode = 'cooling' | 'heating'
export type SpaceType = keyof typeof simulationConfig.spaceTypes

export interface SimulationInputs {
  outdoorTemp: number
  occupancy: number
  area: number
  height: number
  spaceType: SpaceType
  acEnabled: boolean
  setpoint: number
  pidKp: number
  pidKi: number
  pidKd: number
  waterSourceMode: WaterSourceMode
}

export interface DesignAirflows {
  outdoorAirFlow: number
  minimumSupplyFlow: number
  maximumSupplyFlow: number
  designSensibleLoadKw: number
  airChangesAtMaximum: number
}

export interface SimulationState {
  indoorTemp: number
  co2: number
  humidity: number
  outdoorContribution: number
  occupancyContribution: number
  hvacAccumulated: number
  mode: HvacMode
  supplyFlow: number
  outdoorAirFlow: number
  returnAirFlow: number
  airChanges: number
  supplyTemp: number
  supplyHumidity: number
  damperPosition: number
  fanFrequency: number
  waterValve: number
  energyKw: number
  pidIntegral: number
  pidDerivative: number
  pidPreviousReturnTemp: number
  pidOutput: number
  modeElapsedMinutes: number
  wasAcEnabled: boolean
  waterSourceMode: WaterSourceMode
}

export interface SimulationPoint {
  t: number
  indoorTemp: number
  co2: number
  humidity: number
  comfort: number
  supplyFlow: number
}

export interface ComfortAssessment {
  score: number
  temperatureScore: number
  airQualityScore: number
  humidityScore: number
}

const AIR_DENSITY_KG_M3 = 1.2
const AIR_SPECIFIC_HEAT_J_KG_K = 1006
const WATER_VAPOR_PRESSURE_RATIO = 0.62198

export const MAX_SIMULATION_MINUTES = 240
export const PID_TUNING = simulationConfig.hvac.pid
export const TEMPERATURE_TARGET = simulationConfig.comfort.temperature.ideal
export const SPACE_TYPE_OPTIONS = Object.entries(simulationConfig.spaceTypes).map(([value, item]) => ({
  value: value as SpaceType,
  label: item.label
}))
export const DEFAULT_INPUTS: SimulationInputs = {
  outdoorTemp: 32,
  occupancy: 20,
  area: simulationConfig.room.area,
  height: simulationConfig.room.height,
  spaceType: simulationConfig.room.spaceType as SpaceType,
  acEnabled: true,
  setpoint: simulationConfig.hvac.setpoint,
  pidKp: simulationConfig.hvac.pid.kp,
  pidKi: simulationConfig.hvac.pid.ki,
  pidKd: simulationConfig.hvac.pid.kd,
  waterSourceMode: 'cooling'
}

export function recommendWaterSourceMode(
  outdoorTemp: number,
  setpoint: number,
  currentMode: WaterSourceMode
): WaterSourceMode {
  if (outdoorTemp <= setpoint - 2) return 'heating'
  if (outdoorTemp >= setpoint + 2) return 'cooling'
  return currentMode
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value))
}

function moveTowards(current: number, target: number, maximumChange: number) {
  if (current < target) return Math.min(current + maximumChange, target)
  if (current > target) return Math.max(current - maximumChange, target)
  return current
}

function saturationVaporPressure(temperature: number) {
  return 0.61078 * Math.exp((17.2694 * temperature) / (temperature + 237.3))
}

function humidityRatio(relativeHumidity: number, temperature: number) {
  const vaporPressure = clamp(relativeHumidity, 0, 100) / 100 * saturationVaporPressure(temperature)
  return WATER_VAPOR_PRESSURE_RATIO * vaporPressure / Math.max(1, 101.325 - vaporPressure)
}

function relativeHumidityFromRatio(ratio: number, temperature: number) {
  const vaporPressure = 101.325 * Math.max(0, ratio) / (WATER_VAPOR_PRESSURE_RATIO + Math.max(0, ratio))
  return clamp(vaporPressure / saturationVaporPressure(temperature) * 100, 0, 100)
}

export function calculateDesignAirflows(inputs: SimulationInputs): DesignAirflows {
  const room = simulationConfig.room
  const hvac = simulationConfig.hvac
  const space = simulationConfig.spaceTypes[inputs.spaceType]
  const area = Math.max(1, inputs.area)
  const volume = Math.max(1, area * inputs.height)
  const outdoorAirFlow = Math.max(0,
    inputs.occupancy * space.peopleOutdoorAirM3h + area * space.areaOutdoorAirM3h)
  const envelopeUa = area * room.envelopeUaWPerM2K
  const envelopeLoad = envelopeUa * Math.abs(inputs.outdoorTemp - inputs.setpoint)
  const useFactor = inputs.occupancy > 0 ? 1 : 0.15
  const internalLoad = inputs.occupancy * room.occupantSensibleW
    + area * ((room.lightingWPerM2 + room.equipmentWPerM2) * useFactor + room.solarWPerM2)
  const designSensibleLoadW = inputs.waterSourceMode === 'cooling'
    ? Math.max(0, envelopeUa * (inputs.outdoorTemp - inputs.setpoint)) + internalLoad
    : Math.max(0, envelopeLoad - internalLoad)
  const designDeltaT = inputs.waterSourceMode === 'cooling'
    ? hvac.designCoolingDeltaT
    : hvac.designHeatingDeltaT
  const loadBasedFlow = designSensibleLoadW <= 0
    ? 0
    : designSensibleLoadW / (AIR_DENSITY_KG_M3 * AIR_SPECIFIC_HEAT_J_KG_K * designDeltaT) * 3600
  const minimumSupplyFlow = Math.max(outdoorAirFlow, volume * hvac.minimumSupplyAirChanges)
  const maximumLimit = volume * hvac.maximumSupplyAirChanges
  const maximumSupplyFlow = clamp(
    Math.max(minimumSupplyFlow, loadBasedFlow * 1.15),
    minimumSupplyFlow,
    Math.max(minimumSupplyFlow, maximumLimit)
  )

  return {
    outdoorAirFlow: Math.round(Math.min(outdoorAirFlow, maximumSupplyFlow)),
    minimumSupplyFlow: Math.round(minimumSupplyFlow),
    maximumSupplyFlow: Math.round(maximumSupplyFlow),
    designSensibleLoadKw: Math.round(designSensibleLoadW) / 1000,
    airChangesAtMaximum: maximumSupplyFlow / volume
  }
}

export function calculateFanFloor(inputs: SimulationInputs) {
  const pid = simulationConfig.hvac.pid
  const design = calculateDesignAirflows(inputs)
  const minimumRatio = design.maximumSupplyFlow > 0
    ? clamp(design.minimumSupplyFlow / design.maximumSupplyFlow, 0, 1)
    : 0
  return clamp(
    Math.max(pid.minFanFrequency, pid.maxFanFrequency * minimumRatio),
    0,
    pid.maxFanFrequency
  )
}

export function assessComfort(indoorTemp: number, co2: number, humidity = 50): ComfortAssessment {
  const comfort = simulationConfig.comfort
  const temperatureDeviation = Math.abs(indoorTemp - comfort.temperature.ideal)
  const temperaturePenalty = temperatureDeviation <= comfort.temperature.breakpointDeviation
    ? temperatureDeviation * comfort.temperature.innerPenaltyPerDegree
    : comfort.temperature.breakpointDeviation * comfort.temperature.innerPenaltyPerDegree
      + (temperatureDeviation - comfort.temperature.breakpointDeviation) * comfort.temperature.outerPenaltyPerDegree
  const temperatureScore = clamp(100 - temperaturePenalty, 0, 100)

  const airQualityScore = co2 <= comfort.co2.acceptable
    ? 100 - ((Math.max(comfort.co2.ideal, co2) - comfort.co2.ideal)
      / (comfort.co2.acceptable - comfort.co2.ideal)) * (100 - comfort.co2.acceptableScore)
    : co2 <= comfort.co2.critical
      ? comfort.co2.acceptableScore - ((co2 - comfort.co2.acceptable)
        / (comfort.co2.critical - comfort.co2.acceptable))
        * (comfort.co2.acceptableScore - comfort.co2.criticalScore)
      : comfort.co2.criticalScore - ((co2 - comfort.co2.critical)
        / (comfort.co2.zeroScore - comfort.co2.critical)) * comfort.co2.criticalScore

  const humidityDeviation = Math.abs(humidity - comfort.humidity.ideal)
  const humidityPenalty = humidityDeviation <= comfort.humidity.breakpointDeviation
    ? humidityDeviation * comfort.humidity.innerPenaltyPerPercent
    : comfort.humidity.breakpointDeviation * comfort.humidity.innerPenaltyPerPercent
      + (humidityDeviation - comfort.humidity.breakpointDeviation) * comfort.humidity.outerPenaltyPerPercent
  const humidityScore = clamp(100 - humidityPenalty, 0, 100)

  const weightTotal = comfort.temperature.weight + comfort.co2.weight + comfort.humidity.weight
  const score = (
    temperatureScore * comfort.temperature.weight
    + clamp(airQualityScore, 0, 100) * comfort.co2.weight
    + humidityScore * comfort.humidity.weight
  ) / weightTotal

  return {
    score: clamp(score, 0, 100),
    temperatureScore,
    airQualityScore: clamp(airQualityScore, 0, 100),
    humidityScore
  }
}

export function computeComfort(indoorTemp: number, co2: number, humidity = 50) {
  return assessComfort(indoorTemp, co2, humidity).score
}

export function createInitialSimulationState(inputs: SimulationInputs = DEFAULT_INPUTS): SimulationState {
  const design = calculateDesignAirflows(inputs)
  const fanFrequency = inputs.acEnabled ? calculateFanFloor(inputs) : 0
  const supplyFlow = inputs.acEnabled
    ? design.maximumSupplyFlow * fanFrequency / simulationConfig.hvac.pid.maxFanFrequency
    : 0
  const outdoorAirFlow = inputs.acEnabled ? Math.min(design.outdoorAirFlow, supplyFlow) : 0
  return {
    indoorTemp: simulationConfig.baseline.indoorTemp,
    co2: simulationConfig.baseline.outdoorCo2,
    humidity: simulationConfig.baseline.humidity,
    outdoorContribution: 0,
    occupancyContribution: 0,
    hvacAccumulated: 0,
    mode: 'idle',
    supplyFlow,
    outdoorAirFlow,
    returnAirFlow: Math.max(0, supplyFlow - outdoorAirFlow),
    airChanges: supplyFlow / Math.max(1, inputs.area * inputs.height),
    supplyTemp: simulationConfig.baseline.indoorTemp,
    supplyHumidity: simulationConfig.baseline.humidity,
    damperPosition: supplyFlow > 0 ? outdoorAirFlow / supplyFlow * 100 : 0,
    fanFrequency,
    waterValve: 0,
    energyKw: inputs.acEnabled ? 0.4 + (fanFrequency / simulationConfig.hvac.pid.maxFanFrequency) ** 3 * 4.5 : 0,
    pidIntegral: 0,
    pidDerivative: 0,
    pidPreviousReturnTemp: simulationConfig.baseline.indoorTemp,
    pidOutput: 0,
    modeElapsedMinutes: 0,
    wasAcEnabled: inputs.acEnabled,
    waterSourceMode: inputs.waterSourceMode
  }
}

export function toSimulationPoint(time: number, state: SimulationState): SimulationPoint {
  return {
    t: Math.round(time * 10) / 10,
    indoorTemp: Math.round(state.indoorTemp * 100) / 100,
    co2: Math.round(state.co2),
    humidity: Math.round(state.humidity * 10) / 10,
    comfort: Math.round(computeComfort(state.indoorTemp, state.co2, state.humidity) * 10) / 10,
    supplyFlow: Math.round(state.supplyFlow)
  }
}

function resolveMode(state: SimulationState, error: number, pidOutput: number, waterSourceMode: WaterSourceMode): HvacMode {
  const pid = simulationConfig.hvac.pid
  if (state.mode !== 'idle' && state.mode !== waterSourceMode) return 'idle'
  if (state.mode === 'idle') {
    if (state.waterValve > pid.closedValveThreshold) return 'idle'
    if (waterSourceMode === 'cooling'
      && (error >= pid.modeEnterError || (error > pid.modeExitError && pidOutput >= pid.modeEnterOutput))) return 'cooling'
    if (waterSourceMode === 'heating'
      && (error <= -pid.modeEnterError || (error < -pid.modeExitError && pidOutput <= -pid.modeEnterOutput))) return 'heating'
    return 'idle'
  }
  const canExit = state.modeElapsedMinutes >= pid.modeMinHoldMinutes && state.waterValve <= pid.closedValveThreshold
  if (state.mode === 'cooling' && canExit && error <= pid.modeExitError && pidOutput <= pid.modeExitOutput) return 'idle'
  if (state.mode === 'heating' && canExit && error >= -pid.modeExitError && pidOutput >= -pid.modeExitOutput) return 'idle'
  return state.mode
}

function stepControlInterval(state: SimulationState, inputs: SimulationInputs, dtMinutes: number): SimulationState {
  const baseline = simulationConfig.baseline
  const room = simulationConfig.room
  const hvac = simulationConfig.hvac
  const pid = hvac.pid
  const design = calculateDesignAirflows(inputs)
  const area = Math.max(1, inputs.area)
  const volume = Math.max(1, area * inputs.height)
  const fanRestarting = inputs.acEnabled && !state.wasAcEnabled
  const sourceChanging = state.waterSourceMode !== inputs.waterSourceMode
  const controllerRestarting = fanRestarting || sourceChanging
  const error = state.indoorTemp - inputs.setpoint
  const integralError = Math.abs(error) <= pid.integralDeadband ? 0 : error - Math.sign(error) * pid.integralDeadband
  const previousReturnTemp = controllerRestarting ? state.indoorTemp : state.pidPreviousReturnTemp
  const derivativeRaw = (state.indoorTemp - previousReturnTemp) / Math.max(dtMinutes, 0.0001)
  const derivativeAlpha = pid.derivativeFilterMinutes / (pid.derivativeFilterMinutes + dtMinutes)
  const pidDerivative = controllerRestarting ? 0 : derivativeAlpha * state.pidDerivative + (1 - derivativeAlpha) * derivativeRaw
  const integralBase = controllerRestarting || state.mode === 'idle' ? 0 : state.pidIntegral
  const rawPidOutput = inputs.pidKp * error + integralBase + inputs.pidKd * pidDerivative
  const saturatedPidOutput = clamp(rawPidOutput, pid.outputMin, pid.outputMax)
  const pidOutput = !inputs.acEnabled ? 0
    : inputs.waterSourceMode === 'cooling' ? Math.max(0, saturatedPidOutput) : Math.min(0, saturatedPidOutput)
  const mode = inputs.acEnabled ? resolveMode(state, error, pidOutput, inputs.waterSourceMode) : 'idle'
  const pidIntegral = !inputs.acEnabled || mode === 'idle' ? 0 : clamp(
    integralBase + (inputs.pidKi * integralError + pid.antiWindupGain * (saturatedPidOutput - rawPidOutput)) * dtMinutes,
    mode === 'cooling' ? 0 : pid.outputMin,
    mode === 'heating' ? 0 : pid.outputMax
  )
  const demand = mode === 'cooling' ? Math.max(0, pidOutput) : mode === 'heating' ? Math.max(0, -pidOutput) : 0
  const fanFloor = calculateFanFloor(inputs)
  const fanTarget = inputs.acEnabled ? fanFloor + (pid.maxFanFrequency - fanFloor) * demand : 0
  const fanFrequency = inputs.acEnabled
    ? moveTowards(fanRestarting ? fanFloor : state.fanFrequency, fanTarget, pid.fanRampHzPerMinute * dtMinutes)
    : 0
  const valveTarget = inputs.acEnabled ? demand * 100 : 0
  const valveRamp = valveTarget >= state.waterValve ? pid.valveOpenRampPercentPerMinute : pid.valveCloseRampPercentPerMinute
  const waterValve = !inputs.acEnabled || sourceChanging ? 0 : moveTowards(state.waterValve, valveTarget, valveRamp * dtMinutes)
  const supplyFlow = inputs.acEnabled ? design.maximumSupplyFlow * fanFrequency / pid.maxFanFrequency : 0
  const outdoorAirFlow = inputs.acEnabled ? Math.min(design.outdoorAirFlow, supplyFlow) : 0
  const returnAirFlow = Math.max(0, supplyFlow - outdoorAirFlow)
  const outdoorFraction = supplyFlow > 0 ? outdoorAirFlow / supplyFlow : 0
  const mixedAirTemp = state.indoorTemp + (inputs.outdoorTemp - state.indoorTemp) * outdoorFraction
  const coilTarget = mode === 'cooling' ? hvac.coolingSupplyTemp : mode === 'heating' ? hvac.heatingSupplyTemp : mixedAirTemp
  const supplyTemp = mixedAirTemp + (coilTarget - mixedAirTemp) * waterValve / 100

  const envelopeHeatW = area * room.envelopeUaWPerM2K * (inputs.outdoorTemp - state.indoorTemp)
  const useFactor = inputs.occupancy > 0 ? 1 : 0.15
  const occupantHeatW = inputs.occupancy * room.occupantSensibleW
  const areaHeatW = area * ((room.lightingWPerM2 + room.equipmentWPerM2) * useFactor + room.solarWPerM2)
  const supplyHeatW = AIR_DENSITY_KG_M3 * AIR_SPECIFIC_HEAT_J_KG_K * supplyFlow / 3600 * (supplyTemp - state.indoorTemp)
  const thermalCapacityKjK = area * room.thermalMassKjPerM2K
    + AIR_DENSITY_KG_M3 * AIR_SPECIFIC_HEAT_J_KG_K * volume / 1000
  const outdoorContribution = envelopeHeatW * 60 * dtMinutes / (thermalCapacityKjK * 1000)
  const occupancyContribution = (occupantHeatW + areaHeatW) * 60 * dtMinutes / (thermalCapacityKjK * 1000)
  const hvacAccumulated = supplyHeatW * 60 * dtMinutes / (thermalCapacityKjK * 1000)
  const indoorTemp = clamp(state.indoorTemp + outdoorContribution + occupancyContribution + hvacAccumulated, 5, 45)

  const co2GenerationPpmPerMinute = inputs.occupancy * room.co2GenerationLitersPerSecond * 0.001 * 60 / volume * 1_000_000
  const co2DilutionPpmPerMinute = outdoorAirFlow / 60 / volume * (baseline.outdoorCo2 - state.co2)
  const co2 = clamp(state.co2 + (co2GenerationPpmPerMinute + co2DilutionPpmPerMinute) * dtMinutes, baseline.outdoorCo2, 5000)

  const roomHumidityRatio = humidityRatio(state.humidity, state.indoorTemp)
  const outdoorHumidityRatio = humidityRatio(baseline.outdoorHumidity, inputs.outdoorTemp)
  const mixedHumidityRatio = roomHumidityRatio + (outdoorHumidityRatio - roomHumidityRatio) * outdoorFraction
  const coolingLimitRatio = humidityRatio(90, hvac.coolingCoilDewPoint)
  const supplyHumidityRatio = mode === 'cooling'
    ? mixedHumidityRatio + (Math.min(mixedHumidityRatio, coolingLimitRatio) - mixedHumidityRatio) * waterValve / 100
    : mixedHumidityRatio
  const dryAirMassKg = AIR_DENSITY_KG_M3 * volume
  const supplyMoistureKgPerMinute = AIR_DENSITY_KG_M3 * supplyFlow / 60 * (supplyHumidityRatio - roomHumidityRatio)
  const occupantMoistureKgPerMinute = inputs.occupancy * room.occupantMoistureKgPerHour / 60
  const nextHumidityRatio = clamp(
    roomHumidityRatio + (supplyMoistureKgPerMinute + occupantMoistureKgPerMinute) * dtMinutes / dryAirMassKg,
    humidityRatio(15, indoorTemp),
    humidityRatio(85, indoorTemp)
  )
  const humidity = relativeHumidityFromRatio(nextHumidityRatio, indoorTemp)
  const supplyHumidity = relativeHumidityFromRatio(supplyHumidityRatio, supplyTemp)
  const fanRatio = pid.maxFanFrequency > 0 ? fanFrequency / pid.maxFanFrequency : 0
  const coilLoadKw = Math.abs(AIR_DENSITY_KG_M3 * AIR_SPECIFIC_HEAT_J_KG_K * supplyFlow / 3600 * (supplyTemp - mixedAirTemp)) / 1000

  return {
    indoorTemp,
    co2,
    humidity,
    outdoorContribution,
    occupancyContribution,
    hvacAccumulated,
    mode,
    supplyFlow,
    outdoorAirFlow,
    returnAirFlow,
    airChanges: supplyFlow / volume,
    supplyTemp,
    supplyHumidity,
    damperPosition: supplyFlow > 0 ? outdoorAirFlow / supplyFlow * 100 : 0,
    fanFrequency,
    waterValve,
    energyKw: inputs.acEnabled ? 0.4 + fanRatio ** 3 * 4.5 + coilLoadKw / (mode === 'cooling' ? 3 : 0.9) : 0,
    pidIntegral,
    pidDerivative: inputs.acEnabled ? pidDerivative : 0,
    pidPreviousReturnTemp: state.indoorTemp,
    pidOutput,
    modeElapsedMinutes: !inputs.acEnabled || controllerRestarting ? 0 : mode === state.mode ? state.modeElapsedMinutes + dtMinutes : 0,
    wasAcEnabled: inputs.acEnabled,
    waterSourceMode: inputs.waterSourceMode
  }
}

export function stepComfortSimulation(state: SimulationState, inputs: SimulationInputs, dtMinutes: number): SimulationState {
  const totalMinutes = Math.max(0, dtMinutes)
  if (totalMinutes === 0) return state
  const intervalCount = Math.ceil(totalMinutes / simulationConfig.hvac.pid.controlStepMinutes)
  const intervalMinutes = totalMinutes / intervalCount
  let nextState = state
  for (let index = 0; index < intervalCount; index += 1) nextState = stepControlInterval(nextState, inputs, intervalMinutes)
  return nextState
}

export function calculateStability(history: readonly SimulationPoint[], simTime: number) {
  const rules = simulationConfig.stability
  const recent = history.filter((point) => point.t >= simTime - rules.windowMinutes)
  if (recent.length < 2 || simTime < rules.windowMinutes) {
    return { ready: false, tempStable: false, co2Stable: false, tempRate: 0, co2Rate: 0 }
  }
  const first = recent[0]
  const last = recent[recent.length - 1]
  if (first === undefined || last === undefined) {
    return { ready: false, tempStable: false, co2Stable: false, tempRate: 0, co2Rate: 0 }
  }
  const elapsed = Math.max(0.1, last.t - first.t)
  const tempRate = (last.indoorTemp - first.indoorTemp) / elapsed
  const co2Rate = (last.co2 - first.co2) / elapsed
  return {
    ready: true,
    tempStable: Math.abs(tempRate) < rules.temperatureRate,
    co2Stable: Math.abs(co2Rate) < rules.co2Rate,
    tempRate,
    co2Rate
  }
}
